import React, { useState, useEffect } from 'react';
import { Calendar, Badge, Select, Spin } from 'antd';
import { UndoOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { fetchWooAppointmentSlots, parseSlotDate } from '../utils/wooCalendarConfig';

const { Option } = Select;

const toCalendarDay = (value) => {
  if (!value) return dayjs(null);
  if (dayjs.isDayjs(value)) return value;
  if (typeof value.format === 'function') return dayjs(value.format('YYYY-MM-DD'));
  if (typeof value.toDate === 'function') return dayjs(value.toDate());
  return dayjs(value);
};

const locationBadgeColor = (location, productName) => {
  const label = location || productName || '';
  if (label.includes('Kelowna')) return '#103B77';
  if (label.includes('Surrey')) return '#95DE64';
  return '#2db7f5';
};

export default function EmbedCalendarWoo() {
  const maxDate = dayjs().add(365, 'day').format('YYYY-MM-DD');
  const [slots, setSlots] = useState([]);
  const [filteredSlots, setFilteredSlots] = useState([]);
  const [locations, setLocations] = useState([]);
  const [courseNames, setCourseNames] = useState([]);
  const [locationFilter, setLocationFilter] = useState('');
  const [courseFilter, setCourseFilter] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchWooAppointmentSlots(maxDate)
      .then((records) => {
        setSlots(records);
        setFilteredSlots(records);
        setUniqueLocations(records);
        setUniqueCourseNames(records);
      })
      .catch((err) => console.error('Failed to fetch WooCommerce appointment slots:', err))
      .finally(() => setLoading(false));
  }, [maxDate]);

  useEffect(() => {
    if (!slots.length) return;

    if (!locationFilter && !courseFilter) {
      setFilteredSlots(slots);
      return;
    }

    let result = slots;
    if (locationFilter) result = handleLocationFilter(locationFilter, result);
    if (courseFilter) result = handleCourseFilter(courseFilter, result);
    setFilteredSlots(result);
  }, [locationFilter, courseFilter, slots]);

  const dateCellRender = (value) => {
    const cellKey = toCalendarDay(value).format('YYYY-MM-DD');
    const daySlots = filteredSlots.filter(
      (slot) => slot.calendarDay === cellKey || parseSlotDate(slot.date).format('YYYY-MM-DD') === cellKey
    );

    if (!daySlots.length) return null;

    return (
      <ul className="events" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {daySlots.map((slot, index) => (
          <li key={`${slot.product_id}-${cellKey}-${index}`}>
            <a
              className="calendar-event-link"
              href={slot.booking_url}
              target="_blank"
              rel="noreferrer"
              style={{ display: 'block' }}
            >
              <Badge color={locationBadgeColor(slot.location, slot.product_name)} text={slot.product_name} />
            </a>
          </li>
        ))}
      </ul>
    );
  };

  const setUniqueLocations = (records) => {
    const locs = [...new Set(records.map((r) => r.location).filter(Boolean))].sort();
    setLocations(locs);
  };

  const setUniqueCourseNames = (records) => {
    const names = [...new Set(records.map((r) => r.course).filter(Boolean))].sort();
    setCourseNames(names);
  };

  const handleLocationFilter = (filter, slotsToFilter) =>
    slotsToFilter.filter((slot) => slot.location === filter);

  const handleCourseFilter = (filter, slotsToFilter) =>
    slotsToFilter.filter((slot) => slot.course === filter);

  const handleReset = () => {
    setLocationFilter('');
    setCourseFilter('');
  };

  return (
    <div>
      <Select
        placeholder="Filter by location..."
        style={{ width: 240 }}
        onChange={(selected) => setLocationFilter(selected)}
        value={locationFilter || null}
      >
        {locations.map((location) => (
          <Option key={location} value={location}>
            {location}
          </Option>
        ))}
      </Select>

      <Select
        placeholder="Filter by course..."
        style={{ width: 240 }}
        onChange={(selected) => setCourseFilter(selected)}
        value={courseFilter || null}
      >
        {courseNames.map((name) => (
          <Option key={name} value={name}>
            {name}
          </Option>
        ))}
      </Select>

      {(locationFilter || courseFilter) && (
        <UndoOutlined style={{ paddingLeft: '10px' }} onClick={handleReset} />
      )}

      <Spin spinning={loading}>
        <Calendar dateCellRender={dateCellRender} />
      </Spin>
    </div>
  );
}
